import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Headphones, MessageCircle } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { usePlayer } from "@/context/PlayerContext";
import { PageLoader } from "@/components/auth/Guards";
import { Cover, PlayPauseIcon } from "@/components/player/PlayerBits";
import { LikeButton } from "@/components/music/LikeButton";
import { Comments } from "@/components/music/Comments";
import { UserAvatar } from "@/components/ui-nw/UserAvatar";
import { formatCount, formatTime } from "@/lib/format";

export default function TrackPage() {
  const { id } = useParams();
  const { request } = useProfile();
  const { track: current, playing, loading, playTrack } = usePlayer();
  const [track, setTrack] = useState(undefined);

  useEffect(() => { request("get", `/tracks/${id}`).then(setTrack).catch(() => setTrack(null)); }, [id, request]);

  if (track === undefined) return <PageLoader />;
  if (track === null) return (
    <div data-testid="track-not-found" className="py-24 text-center space-y-4">
      <h1 className="font-display text-4xl font-extrabold chrome-text">Track not found</h1>
      <Link to="/music" className="btn-red inline-block px-5 py-2.5 text-sm">Back to music</Link>
    </div>
  );
  const active = current?.id === track.id;

  return (
    <div data-testid="track-page" className="space-y-8 animate-rise">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-nw-surface/70 p-5 sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-nw-red/25 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row gap-6 sm:items-end">
          <Cover track={track} className="aspect-square w-full sm:w-56 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)]" />
          <div className="flex-1 space-y-3 min-w-0">
            <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-slate-300">{track.genre}</span>
            <h1 data-testid="track-title" className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white break-words">{track.title}</h1>
            <p data-testid="track-artist" className="text-slate-300">{track.artist_name}</p>
            {track.owner && (
              <Link to={`/u/${track.owner.username}`} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
                <UserAvatar profile={track.owner} size="h-7 w-7" textSize="text-[10px]" /> @{track.owner.username}
              </Link>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button data-testid="track-page-play-button" onClick={() => playTrack(track, [track])} className="btn-red inline-flex items-center gap-2 px-6 py-3 text-sm">
                <PlayPauseIcon playing={active && playing} loading={active && loading} /> {active && playing ? "Pause" : "Play"}
              </button>
              <LikeButton track={track} size="lg" />
              <span className="inline-flex items-center gap-1.5 text-sm text-slate-400"><MessageCircle className="h-4 w-4" /><span data-testid="track-comment-count">{formatCount(track.comment_count)}</span></span>
              <span className="inline-flex items-center gap-1.5 text-sm text-slate-400"><Headphones className="h-4 w-4" /><span data-testid="track-play-count">{formatCount(track.qualified_plays)}</span> {track.qualified_plays === 1 ? "play" : "plays"}</span>
              <span className="font-mono text-xs text-slate-500">{formatTime(track.duration_seconds)}</span>
            </div>
          </div>
        </div>
        {track.description && <p data-testid="track-description" className="relative mt-6 max-w-2xl text-sm leading-relaxed text-slate-300 whitespace-pre-line">{track.description}</p>}
      </div>
      <Comments trackId={track.id} onCountChange={(d) => setTrack((t) => ({ ...t, comment_count: t.comment_count + d }))} />
    </div>
  );
}
